'use client'

import { useEffect, useMemo, useState } from 'react'
import { AnnouncementBarItem, type AnnouncementItem } from './AnnouncementBarItem'

export type AnnouncementBarRotatorProps = {
  items: Array<AnnouncementItem & { rotate_seconds: number }>
}

const DISMISS_PREFIX = 'sifly-announcement-dismissed-'

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function AnnouncementBarRotator({ items }: AnnouncementBarRotatorProps) {
  const [hydrated, setHydrated] = useState(false)
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set())
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(false)

  useEffect(() => {
    // Mount-time hydration: reads browser-only APIs (matchMedia, sessionStorage)
    // that can't run during SSR/render, so the setState-in-effect is required.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHydrated(true)
    setReducedMotion(prefersReducedMotion())
    if (typeof sessionStorage === 'undefined') return
    const dismissed = new Set<string>()
    for (const item of items) {
      if (sessionStorage.getItem(DISMISS_PREFIX + item.id) === '1') {
        dismissed.add(item.id)
      }
    }
    setDismissedIds(dismissed)
  }, [items])

  const visible = useMemo(
    () => items.filter((i) => !dismissedIds.has(i.id)),
    [items, dismissedIds],
  )

  // Wrap index when the visible list shrinks (e.g. after a dismiss).
  useEffect(() => {
    // Clamp the rotator index after the visible set changes — a corrective
    // setState driven by derived length, not a render-loop.
    if (visible.length === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIndex(0)
    } else if (index >= visible.length) {
      setIndex(0)
    }
  }, [visible.length, index])

  // Auto-advance.
  useEffect(() => {
    if (!hydrated || reducedMotion) return
    if (visible.length <= 1 || paused) return
    const current = visible[index]
    const ms = Math.max(3, Math.min(30, current.rotate_seconds)) * 1000
    const t = window.setTimeout(() => {
      setIndex((i) => (i + 1) % visible.length)
    }, ms)
    return () => window.clearTimeout(t)
  }, [hydrated, reducedMotion, visible, index, paused])

  if (!hydrated) {
    // Render the first item statically so SSR HTML is non-empty (no layout
    // shift). Controls and dismiss are wired up after hydration.
    if (items.length === 0) return null
    const first = items[0]
    return <AnnouncementBarItem item={first} />
  }

  if (visible.length === 0) return null

  const current = visible[index]
  const showControls = visible.length > 1

  function advance(delta: number) {
    setIndex((i) => {
      const next = (i + delta + visible.length) % visible.length
      return next
    })
  }

  function dismiss(id: string) {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(DISMISS_PREFIX + id, '1')
    }
    setDismissedIds((prev) => {
      const next = new Set(prev)
      next.add(id)
      return next
    })
  }

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <AnnouncementBarItem
        item={current}
        showControls={showControls}
        onPrev={() => advance(-1)}
        onNext={() => advance(1)}
        onDismiss={current.dismissible ? () => dismiss(current.id) : undefined}
      />
    </div>
  )
}
